import { useCallback, useEffect, useMemo } from 'react';
import { useBankStore } from '@/stores/bankStore';

export interface LogoAccount {
  integrationType: string;
  bankCode: string;
  iban?: string;
  bankLogoUrl?: string;
}

/**
 * Returns a resolver that maps an account to `<BankLogo>` props ({ code, src }).
 *
 * Non-SIBS integrations (LusoPay/Revolut/Stripe/PayPal/cash) key the self-hosted
 * asset by their `bankCode`. SIBS banks resolve their real aspspCde + public CDN
 * logo URL from the bank list via the account's IBAN (positions 5-8 are the NIB),
 * because the stored bankCode is a legacy internal value, not a SIBS code.
 *
 * Loads the bank list once on mount (the store no-ops if already cached).
 */
export function useBankLogo() {
  const { banks, fetchBanks } = useBankStore();

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  const banksByNib = useMemo(() => {
    const map = new Map<string, (typeof banks)[number]>();
    for (const bank of banks) if (bank.bankCode) map.set(bank.bankCode, bank);
    return map;
  }, [banks]);

  // Stable until the bank list changes, so a list can memoise what it derives from it.
  return useCallback(
    (account: LogoAccount): { code: string; src?: string } => {
      if (account.integrationType === 'Sibs') {
        const iban = (account.iban ?? '').replace(/\s/g, '').toUpperCase();
        const nib = iban.startsWith('PT') && iban.length >= 8 ? iban.slice(4, 8) : '';
        const bank = banksByNib.get(nib);
        return { code: bank?.aspspCde ?? account.bankCode, src: bank?.logoLocation ?? account.bankLogoUrl };
      }
      return { code: account.bankCode, src: account.bankLogoUrl };
    },
    [banksByNib],
  );
}
